import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Table, Button, Card, Col, Modal, Row, Spinner } from 'react-bootstrap';
import PageHeader from 'components/common/PageHeader';
import UserContext from 'app/context/UserContext';
import axios from 'axios';
import { changeTaskStatus, one, screenshotlist,changeTaskPaymentStatus } from 'app/api/TaskService';
import { toast } from 'react-toastify';

const TaskStatus = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

function TaskProgress() {
  const { id } = useParams();
  const { user } = useContext(UserContext);
  const [task, setTask] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyScreenshots, setHistoryScreenshots] = useState([]);

  const [completionState, setCompletionState] = useState({
    completedTaskPricingId: '',
    status: '',
    rejectionMessage: null,
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionInput, setRejectionInput] = useState('');

  const handleAccept = (id) => {
    setSelectedTaskId(id);
    setConfirmAction('accept');
    setShowConfirm(true);
  };

  const handleReject = (id) => {
    setSelectedTaskId(id);
    setConfirmAction('reject');
    setShowConfirm(true);
  };

  const confirmActionHandler = () => {
    if (confirmAction === 'accept') {
      const newState = {
        completedTaskPricingId: selectedTaskId,
        status: TaskStatus.APPROVED,
        rejectionMessage: null,
      };
      setCompletionState(newState);
      changeScreenshotStatus(newState);
    } else if (confirmAction === 'reject') {
      setShowRejectionModal(true);
    }
    setShowConfirm(false);
  };

  const submitRejection = () => {
    const newState = {
      completedTaskPricingId: selectedTaskId,
      status: TaskStatus.REJECTED,
      rejectionMessage: rejectionInput,
    };
    setCompletionState(newState);
    changeScreenshotStatus(newState);
    setShowRejectionModal(false);
    setRejectionInput('');
  };

  async function changeScreenshotStatus(state) {
    setLoading(true)
    try {
      const body =
        state.status === TaskStatus.REJECTED
          ? {
              status: state.status,
              rejectionMessage: state.rejectionMessage,
            }
          : {
              status: state.status,
            };

      const config = changeTaskStatus(
        user.token,
        state.completedTaskPricingId,
        body
      );

      const response = await axios.request(config);
      // console.log('Status change response:', response);

      toast.success('Screenshot status updated successfully');
      setLoading(false);

      // Refresh screenshots list
      fetchScreenshots();

      // Reset state
      setCompletionState({
        completedTaskPricingId: '',
        status: '',
        rejectionMessage: null,
      });
    } catch (error) {
      console.error('Error updating screenshot status:', error);
      toast.error('Failed to update screenshot status');
      setLoading(false);
    }
  }

  const fetchTaskDetails = async () => {
    try {
      const config = one(user.token, id);
      const response = await axios.request(config);
      if (response.data?.data) {
        setTask(response.data.data);
      } else {
        toast.error('Task not found');
      }
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      toast.error('Error fetching task details');
    } finally {
      setLoading(false);
    }
  };

  const fetchScreenshots = async () => {
    try {
      const config = screenshotlist(user.token, id);
      const response = await axios.request(config);
      if (response.data?.data) {
        setScreenshots(response.data.data);
        setHistoryScreenshots(response.data.history_screenShot || []);
      }
    } catch (error) {
      console.error('Failed to fetch task screenshots:', error);
      toast.error('Error fetching task screenshots');
    } finally {
      setLoading(false);
    }
  };

  const changePaymentStatus = async (status) => {
    setLoading(true);
   try {
      const config = changeTaskPaymentStatus(user.token, id, status);
      const response = await axios.request(config);
      if(response){
        toast.dismiss()
        toast.success('Payment status changed successfully')
        fetchTaskDetails();
      }
    } catch (error) {
      console.error('Failed to change status:', error);
      toast.error('Error changing payment status');
    } finally {
      setLoading(false);
    }

  }

  useEffect(() => {
    fetchTaskDetails();
    fetchScreenshots();
  }, [id]);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`Task Progress | ${process.env.REACT_APP_PROJECT_NAME}`}</title>
      </Helmet>

      <PageHeader col={{ xs: 6 }} title="Task Progress" className="mb-3" />

      {loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" />
        </div>
      ) : task ? (
        <>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Business Info</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div>
                    <strong>Business Name:</strong> {task.businessName}
                  </div>
                  <div>
                    <strong>Leads:</strong> {task.noOfLeads}
                  </div>
                  <div>
                    <strong>Remaining:</strong> {task.remainingOfLeads}
                  </div>
                  <div>
                    <strong>Performing:</strong> {task.performingNoOfLeads}
                  </div>
                  {task.taskLocation && <div>
                    <strong>Task Location:</strong> {task.taskLocation}
                  </div>}
                  {task.paymentMethod && <div>
                    <strong>Payment Method:</strong> {task.paymentMethod} Balance
                  </div>}
                </Col>
                <Col
                  md={6}
                  className="d-flex justify-content-start align-items-start gap-3"
                >
                  <div>
                    <h6>Logo</h6>
                    <img
                      src={task.logoImage}
                      alt="Logo"
                      width={100}
                      height={100}
                    />
                  </div>
                  <div>
                    <h6>Background</h6>
                    <img
                      src={task.backgroundImage}
                      alt="Background"
                      width={100}
                      height={100}
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm mt-3">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                Chosen Tasks ({task?.assignedPrices.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {task?.assignedPrices?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Rewards</th>
                        <th>Cost</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {task.assignedPrices.map((item) => (
                        <tr key={item.id}>
                          <td><h6>{item.title}</h6>
                          {item.task_description && <p style={{fontSize: "15px"}}>{item.task_description}</p>}
                          </td>
                          <td>{item.type}</td>
                          <td>{item.noOfRewards}</td>
                          <td>{item.noOfCost}</td>
                          <td>
                            <a
                              href={item.relativePath}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Preview
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0">No tasks selected.</p>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm mt-3">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                Payment Information
              </h5>
            </Card.Header>
            <Card.Body>
              {task.payment_task?.date && task.payment_task?.total_cost ? (
                <Table bordered size="sm" className='table-bordered table-hover'>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Total Cost</th>
                      <th>Status</th>
                      {task.status == "PENDING" && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{task.payment_task.date}</td>
                      <td>{task.payment_task.total_cost}</td>
                      <td className={`${task.status == "ACCEPTED" && "text-success"} ${task.status == "REJECTED" && "text-danger"} ${task.status == "PENDING" && "text-primary"}`} style={{ textTransform: 'capitalize' }}>
                        {task.status.toLowerCase()}
                      </td>
                      {task.status == "PENDING" && <td>
                        <Button variant="success" size="sm" className="me-2"
                        onClick={()=> changePaymentStatus("ACCEPTED")}
                        >
                          Accept
                        </Button>
                        <Button variant="danger" size="sm"
                        onClick={()=> changePaymentStatus("REJECTED")}
                        >
                          Reject
                        </Button>
                      </td>}
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p className="text-primary text-center mb-0">Payment information not found</p>
              )}
            </Card.Body>

            </Card>
            
          <Card className="shadow-sm mt-3">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                Screenshots received ({screenshots.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {screenshots.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Task Title</th>
                        <th>Received From</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    {!loading ? <tbody>
                      {screenshots.map((screenshot) => (
                        <tr key={screenshot.id}>
                          <td>{screenshot.taskPricingTitle}</td>
                          <td>{screenshot.memberName}</td>
                          <td>
                            <a
                              href={screenshot.screenshot}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-sm mx-1"
                            >
                              View
                            </a>
                            <Button
                              variant="success"
                              size="sm"
                              className="mx-1"
                              onClick={() => handleAccept(screenshot.id)}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              className="mx-1"
                              onClick={() => handleReject(screenshot.id)}
                            >
                              Deny
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>: <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    />}
                  </table>
                </div>
              ) : (
                <div className="text-center mt-1 text-muted">
                  No task screenshots are available.
                </div>
              )}
            </Card.Body>

            {/* Confirm Modal */}
            <Modal
              show={showConfirm}
              onHide={() => setShowConfirm(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Action</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to{' '}
                {confirmAction === 'accept' ? 'accept' : 'reject'} this task?
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={confirmActionHandler}>
                  Yes, Confirm
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Rejection Modal */}
            <Modal
              show={showRejectionModal}
              onHide={() => setShowRejectionModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Rejection Reason</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <textarea
                  className="form-control"
                  placeholder="Enter reason for rejection"
                  value={rejectionInput}
                  onChange={(e) => setRejectionInput(e.target.value)}
                  rows={4}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowRejectionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={submitRejection}
                  disabled={!rejectionInput.trim()}
                >
                  Submit Rejection
                </Button>
              </Modal.Footer>
            </Modal>
          </Card>
          <Card className="shadow-sm mt-3">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                Screenshots history ({historyScreenshots.length}) 
              </h5>
            </Card.Header>
            <Card.Body>
              {historyScreenshots.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Task Title</th>
                        <th>Received From</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    {!loading ? <tbody>
                      {historyScreenshots.map((screenshot) => (
                        <tr key={screenshot.id}>
                          <td>{screenshot.taskPricingTitle}</td>
                          <td>{screenshot.memberName}</td>
                          <td>
                            <a
                              href={screenshot.screenshot}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-sm mx-1"
                            >
                              View
                            </a>

                            <button
                                className="btn btn-success btn-sm mx-1"
                                onClick={async (e) => {
                                  e.preventDefault();

                                  try {
                                    const response = await fetch(screenshot.screenshot);
                                    const blob = await response.blob();

                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');

                                    link.href = url;
                                    link.download = `screenshot-${screenshot.taskPricingTitle}.png`;

                                    document.body.appendChild(link);
                                    link.click();

                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                  } catch (err) {
                                    console.error('Download failed', err);
                                    alert('Unable to download file');
                                  }
                                }}
                              >
                                Download
                              </button>

                          </td>

                        </tr>
                      ))}
                    </tbody>: 
                     <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    />
                    }
                  </table>
                </div>
              ) : (
                <div className="text-center mt-1 text-muted">
                  No task screenshots are available.
                </div>
              )}
            </Card.Body>

            {/* Confirm Modal */}
            <Modal
              show={showConfirm}
              onHide={() => setShowConfirm(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Action</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to{' '}
                {confirmAction === 'accept' ? 'accept' : 'reject'} this task?
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={confirmActionHandler}>
                  Yes, Confirm
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Rejection Modal */}
            <Modal
              show={showRejectionModal}
              onHide={() => setShowRejectionModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Rejection Reason</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <textarea
                  className="form-control"
                  placeholder="Enter reason for rejection"
                  value={rejectionInput}
                  onChange={(e) => setRejectionInput(e.target.value)}
                  rows={4}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowRejectionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={submitRejection}
                  disabled={!rejectionInput.trim()}
                >
                  Submit Rejection
                </Button>
              </Modal.Footer>
            </Modal>
          </Card>
        </>
      ) : (
        <div className="text-center mt-5 text-muted">
          No task data available.
        </div>
      )}
    </div>
  );
}

export default TaskProgress;
